import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { backendClient } from '@/lib/api-client';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useMatterLabels } from '@/lib/useMatterLabels';
import type { MatterAccess } from '@/types/base';

interface JoinMatterButtonProps {
  matterAccess: MatterAccess;
  onSuccess?: () => void;
}

const JoinMatterButton = ({ matterAccess, onSuccess }: JoinMatterButtonProps) => {
  const { t } = useTranslation('common');
  const label = useMatterLabels();
  const [isPending, setIsPending] = useState(matterAccess.membership_request_pending);
  const [isRequesting, setIsRequesting] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  // Keep the local pending state in sync with the server. Without this, an owner
  // approving/rejecting the request (which refreshes the list prop) would leave a
  // still-mounted row showing "Cancel", and cancelling then 404s.
  useEffect(() => {
    setIsPending(matterAccess.membership_request_pending);
  }, [matterAccess.membership_request_pending]);

  const isUserMember = matterAccess.current_user_role?.name === 'Member' || matterAccess.current_user_role?.name === 'Owner';
  
  const handleJoinRequest = async () => {
    setIsRequesting(true);
    try {
      await backendClient.post(`/matter/${matterAccess.guid}/request-membership`);
      toast.success(t('join-request-sent'));
      setIsPending(true);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error) {
      console.error('Error requesting membership:', error);
      toast.error(t('error-requesting-membership'));
    } finally {
      setIsRequesting(false);
    }
  };

  const handleCancelRequest = async () => {
    setIsCancelling(true);
    try {
      await backendClient.delete(`/matter/${matterAccess.guid}/request-membership`);
      toast.success(label('join-request-cancelled'));
      setIsPending(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (error: any) {
      console.error('Error cancelling membership request:', error);
      toast.error(error?.response?.data?.detail || label('error-cancelling-membership'));
    } finally {
      setIsCancelling(false);
    }
  };
  
  if (isUserMember) {
    return <Badge variant="outline">{matterAccess.current_user_role?.name}</Badge>;
  }
  
  if (isPending) {
    return (
      <div className="flex items-center gap-3" onClick={(e) => e.stopPropagation()}>
        <span className="whitespace-nowrap text-sm text-muted-foreground">{t('request-pending')}</span>
        <Button
          variant="destructive"
          size="sm"
          disabled={isCancelling}
          onClick={(e) => {
            e.stopPropagation();
            handleCancelRequest();
          }}
        >
          {isCancelling ? label('cancelling') + '...' : t('cancel-request')}
        </Button>
      </div>
    );
  }
  
  return (
    <Button 
      variant="outline" 
      size="sm"
      disabled={isRequesting}
      onClick={(e) => {
        e.stopPropagation();
        handleJoinRequest();
      }}
    >
      {isRequesting ? t('requesting') + '...' : t('request-to-join')}
    </Button>
  );
};

export default JoinMatterButton;
